import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  FlattenedEmployeeRecord,
  CustomProperty,
  PathEvaluator,
  AttributeConfigData,
  AttributeDefinition,
} from "winslow-core";

interface EmployeeRecordCacheContextType {
  employeeRecords: FlattenedEmployeeRecord[];
  setEmployeeRecords: (records: FlattenedEmployeeRecord[]) => void;
  customProperties: CustomProperty[];
  setCustomProperties: (properties: CustomProperty[]) => void;
  isLoaded: boolean;
  setIsLoaded: (loaded: boolean) => void;
}

export const EmployeeRecordCacheContext =
  createContext<EmployeeRecordCacheContextType | null>(null);

export const useEmployeeRecordCache = () => {
  const context = useContext(EmployeeRecordCacheContext);
  if (!context) {
    throw new Error(
      "useEmployeeRecordCache must be used within an EmployeeRecordCacheProvider"
    );
  }
  return context;
};

export const useEmployeeRecordCacheState = () => {
  const [employeeRecords, setEmployeeRecords] = useState<
    FlattenedEmployeeRecord[]
  >([]);
  const [customProperties, setCustomProperties] = useState<CustomProperty[]>(
    []
  );
  const [isLoaded, setIsLoaded] = useState(false);

  return {
    employeeRecords,
    setEmployeeRecords,
    customProperties,
    setCustomProperties,
    isLoaded,
    setIsLoaded,
  };
};

const isHumanReadableValue = (value: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const technicalIdRegex = /^[a-zA-Z0-9_-]{20,}$/;
  const numericOnlyRegex = /^\d+$/;

  if (uuidRegex.test(value)) return false;
  if (technicalIdRegex.test(value)) return false;
  if (numericOnlyRegex.test(value) && value.length > 10) return false;

  return true;
};

const normalizeValueForSuggestion = (value: any): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  let stringValue: string;

  if (typeof value === "string") {
    stringValue = value.trim();
  } else if (typeof value === "object" && value !== null) {
    stringValue = (
      value.name ||
      value.id ||
      value.title ||
      JSON.stringify(value)
    ).trim();
  } else {
    stringValue = String(value).trim();
  }

  if (stringValue && isHumanReadableValue(stringValue)) {
    return stringValue;
  }

  return null;
};

export const useEmployeeRecordAttributeSuggestions = () => {
  const { employeeRecords, customProperties } = useEmployeeRecordCache();

  const attributeConfig = useMemo((): AttributeConfigData => {
    const config: AttributeConfigData = {
      employment: [],
      location: [],
      groups: [],
      custom: [],
    };

    // Add built-in employeeType field
    config.employment.push({
      path: "employeeType",
      displayName: "Employee Type",
      dataType: "string",
      customPropertyType: "employeeType",
    } as AttributeDefinition);

    // Add custom properties
    customProperties.forEach((prop) => {
      const category = prop.group?.toLowerCase() || "custom";
      let categoryKey: keyof AttributeConfigData = "custom";

      if (category === "employment") {
        categoryKey = "employment";
      } else if (category === "location") {
        categoryKey = "location";
      } else if (category === "groups") {
        categoryKey = "groups";
      }

      config[categoryKey].push({
        path: prop.label,
        displayName: prop.display,
        dataType: "string",
        customPropertyType: prop.type,
        customPropertyConfig: prop.settings,
      } as AttributeDefinition);
    });

    return config;
  }, [customProperties]);

  const getSuggestions = useCallback(
    (attributePath: string, searchTerm: string = ""): string[] => {
      if (!employeeRecords || employeeRecords.length === 0 || !attributePath) {
        return [];
      }

      try {
        // Filter to only active employees
        const activeEmployees = employeeRecords.filter(
          (record) => record.active === true
        );

        if (activeEmployees.length === 0) {
          return [];
        }

        const allValues = PathEvaluator.extractAllValues(
          activeEmployees,
          attributePath
        );

        const suggestions = allValues
          .map((value) => normalizeValueForSuggestion(value))
          .filter((value): value is string => value !== null)
          .filter((value) => value.length > 0);

        let filteredSuggestions = suggestions;
        if (searchTerm.trim()) {
          const searchLower = searchTerm.toLowerCase();
          filteredSuggestions = suggestions.filter((value) =>
            value.toLowerCase().includes(searchLower)
          );
        }

        filteredSuggestions.sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );

        return filteredSuggestions.slice(0, 50);
      } catch (error) {
        console.warn(`Error getting suggestions for ${attributePath}:`, error);
        return [];
      }
    },
    [employeeRecords]
  );

  const hasValueForPath = useCallback(
    (path: string): boolean => {
      if (!employeeRecords || employeeRecords.length === 0 || !path) {
        return false;
      }

      // Filter to only active employees
      const activeEmployees = employeeRecords.filter(
        (record) => record.active === true
      );

      return activeEmployees.some((record) => {
        const value = PathEvaluator.extractValue(record, path);

        if (value === null || value === undefined) {
          return false;
        }

        if (Array.isArray(value)) {
          return value.some((item) => {
            const normalized = normalizeValueForSuggestion(item);
            return normalized !== null;
          });
        } else {
          const normalized = normalizeValueForSuggestion(value);
          return normalized !== null;
        }
      });
    },
    [employeeRecords]
  );

  const getAvailableAttributePaths = useCallback((): string[] => {
    if (!attributeConfig) {
      return [];
    }

    const allPaths = new Set<string>();

    Object.values(attributeConfig).forEach((categoryAttributes) => {
      categoryAttributes.forEach((attr: AttributeDefinition) => {
        allPaths.add(attr.path);
      });
    });

    const availablePaths = Array.from(allPaths).filter((path) =>
      hasValueForPath(path)
    );

    return availablePaths.sort();
  }, [attributeConfig, hasValueForPath]);

  const getAttributeMetadata = useCallback(
    (path: string) => {
      if (!attributeConfig) {
        return null;
      }

      for (const categoryAttributes of Object.values(attributeConfig)) {
        const attribute = categoryAttributes.find(
          (attr: AttributeDefinition) => attr.path === path
        );
        if (attribute) {
          return attribute;
        }
      }

      return null;
    },
    [attributeConfig]
  );

  const getAttributeConfig = useCallback((): AttributeConfigData => {
    return attributeConfig;
  }, [attributeConfig]);

  const validatePath = useCallback(
    (path: string): boolean => {
      if (!employeeRecords || employeeRecords.length === 0 || !path) {
        return false;
      }

      return employeeRecords.some((record) =>
        PathEvaluator.validatePath(record, path)
      );
    },
    [employeeRecords]
  );

  const getSuggestionsWithMetadata = useCallback(
    (attributePath: string, searchTerm: string = "") => {
      const suggestions = getSuggestions(attributePath, searchTerm);
      const metadata = getAttributeMetadata(attributePath);

      return {
        suggestions,
        metadata,
        hasValues: suggestions.length > 0,
        isValid: validatePath(attributePath),
      };
    },
    [getSuggestions, getAttributeMetadata, validatePath]
  );

  return {
    getSuggestions,
    getAvailableAttributePaths,
    getAttributeMetadata,
    getAttributeConfig,
    hasValueForPath,
    validatePath,
    getSuggestionsWithMetadata,
  };
};
